import AppCard from '@nxsys/components/AppCard';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync, { OptionType } from '@nxsys/components/AppSelectSync';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Col, Form, Input, Row, Select, Table } from 'antd';
import { FormProps } from 'antd/lib';
import { Moment } from 'moment';
import React, { useEffect, useState } from 'react';

const { Option } = Select;

type RateItemType = {
  key: string;
  rateCode: string;
  rateType: string;
  description: string;
  rate: string;
  unit: string;
  net: string;
  vat: string;
  total: string;
};

type AddNewInvoiceProps = {
  onSave: (values: any) => void;
  isOpen: boolean;
  toggle: () => void;
  data: any;
};

const AddNewTimeSheet: React.FC<AddNewInvoiceProps> = ({ onSave, toggle, isOpen, data }) => {
  const [form] = Form.useForm();
  const [rateItems, setRateItems] = useState<RateItemType[]>([]);
  const { messages, formatMessage } = useTypedIntl();
  const taxWeekOption = {
    label: formatMessage({ id: 'statutory.timeSheet.taxWeekLabel' }, { week: data?.taxWeek ?? '' }),
    value: data?.taxWeek ?? '',
  };

  useEffect(() => {
    if (data) {
      setRateItems([{
        rateCode: `PR${data.type}`,
        rateType: 'Rate',
        description: '',
        rate: data.amount,
        net: data.amount,
        total: data.amount,
        unit: '1',
        vat: 'VAT',
        key: '1',
      }])
      form.setFieldsValue({
        subcontractorName: {
          label: `${data?.forename} ${data?.surname}`,
          key: data?.id,
        },
        taxPeriod: taxWeekOption,
      })
    }
  }, [data])

  const onFinish: FormProps['onFinish'] = (values) => {
    console.log('Success:', values);
    onSave({ ...values, rateItems });
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  const updateRateItem = (
    key: string,
    field: keyof RateItemType,
    value: string,
  ) => {
    setRateItems(
      rateItems.map((item) =>
        item.key === key ? { ...item, [field]: value } : item,
      ),
    );
  };

  const rateColumns = [
    {
      title: messages["statutory.timeSheet.rateCode"],
      dataIndex: 'rateCode',
      key: 'rateCode',
      render: (text: string, record: RateItemType) => (
        <Select
          value={text}
          onChange={(value) => updateRateItem(record.key, 'rateCode', value)}
          style={{ width: '100%' }}
          disabled={true}
        >
          <Option value="PR100">PR100</Option>
          <Option value="PR200">PR200</Option>
          <Option value="PR300">PR300</Option>
        </Select>
      ),
    },
    {
      title: messages["statutory.timeSheet.rateType"],
      dataIndex: 'rateType',
      key: 'rateType',
      render: (text: string, record: RateItemType) => (
        <Input
          value={text}
          onChange={(e) =>
            updateRateItem(record.key, 'rateType', e.target.value)
          }
          disabled={true}
        />
      ),
    },
    {
      title: messages["statutory.timeSheet.description"],
      dataIndex: 'description',
      key: 'description',
      render: (text: string, record: RateItemType) => (
        <Input
          value={text}
          onChange={(e) =>
            updateRateItem(record.key, 'description', e.target.value)
          }
          disabled={true}
        />
      ),
    },
    {
      title: messages["statutory.timeSheet.rate"],
      dataIndex: 'rate',
      key: 'rate',
      render: (text: string, record: RateItemType) => (
        <Input
          value={text}
          onChange={(e) => updateRateItem(record.key, 'rate', e.target.value)}
          disabled={true}
        />
      ),
    },
    {
      title: messages["statutory.timeSheet.unit"],
      dataIndex: 'unit',
      key: 'unit',
      render: (text: string, record: RateItemType) => (
        <Input
          value={text}
          onChange={(e) => updateRateItem(record.key, 'unit', e.target.value)}
          disabled={true}
        />
      ),
    },
    {
      title: 'NET',
      dataIndex: 'net',
      key: 'net',
      render: (text: string, record: RateItemType) => (
        <Input
          value={text}
          onChange={(e) => updateRateItem(record.key, 'net', e.target.value)}
          disabled={true}
        />
      ),
    },
    {
      title: 'VAT',
      dataIndex: 'vat',
      key: 'vat',
      render: (text: string, record: RateItemType) => (
        <Select
          value={text}
          onChange={(value) => updateRateItem(record.key, 'vat', value)}
          style={{ width: '100%' }}
          disabled={true}
        >
          <Option value="Standard">Standard</Option>
          <Option value="Reduced">Reduced</Option>
          <Option value="Zero">Zero</Option>
        </Select>
      ),
    },
    {
      title: messages["statutory.timeSheet.total"],
      dataIndex: 'total',
      key: 'total',
      render: (text: string, record: RateItemType) => (
        <Input
          value={text}
          onChange={(e) => updateRateItem(record.key, 'total', e.target.value)}
          disabled={true}
        />
      ),
    },
  ];

  return (
    <AppModalContainer
      title={messages["statutory.timeSheet.addTimeSheet"]}
      isOpen={isOpen}
      toggle={toggle}
      width={1000}
      actions={[
        {
          name: messages["common.cancel"],
          variant: 'outlined',
          onClick: toggle,
        },
        {
          name: messages["common.save"],
          buttonType: 'primary',
          onClick: () => form.submit(),
        },
      ]}
    >
      <AppCard>
        <AppFormContainer
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
        >
          {/* Invoicing Section */}
          <div style={{ marginBottom: '32px', padding: '12px 16px', }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
              }}
            >
              <span style={{ fontSize: '18px' }}>📄</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                {messages["statutory.timeSheet.invoicing"]}
              </h3>
            </div>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label={messages["statutory.timeSheet.subcontractorName"]}
                  name="subcontractorName"
                  rules={[
                    { required: true, message: messages["statutory.timeSheet.pleaseSelectEmployee"] },
                  ]}
                >
                  <AppSelectSync
                    type="emp&sub"
                    apiType="searchEmployeeSubcontractV2"
                    placeholder={messages["statutory.form.selectEmployee"]}
                    disabled={true}
                  />
                </Form.Item>

                <Form.Item
                  label={messages["statutory.form.agencyName"]}
                  name="agencyName"
                  rules={[{ required: true, message: messages["statutory.timeSheet.pleaseSelectAgency"] }]}
                >
                  <AppSelectSync
                    type="agency"
                    apiType="searchAgency"
                    placeholder={messages["statutory.form.selectAgency"]}
                    onChangeValue={(value: OptionType | OptionType[] | null) => {
                      form.setFieldsValue({
                        agencyName: value,
                      });
                    }}
                  />
                </Form.Item>

                <Form.Item label={messages["statutory.timeSheet.siteId"]} name="siteId">
                  <AppSelectSync
                    type="agencySite"
                    apiType="searchSiteInAgency"
                    placeholder={messages["statutory.timeSheet.selectSite"]}
                  />
                </Form.Item>

                <Form.Item label={messages["statutory.timeSheet.attachment"]} name="attachment">
                  <AppUploadFile
                    onChange={(file: File) => {
                      form.setFieldsValue({ attachment: file });
                    }}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label={messages["statutory.timeSheet.assignment"]} name="assignment">
                  <AppSelectSync
                    type="assignment"
                    apiType="searchAssignment"
                    placeholder={messages["statutory.timeSheet.selectAssignment"]}
                  />
                </Form.Item>

                <Form.Item
                  label={messages["statutory.timeSheet.invoiceDate"]}
                  name="invoiceDate"
                >
                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item name="startDate">
                        <AppDatePicker
                          placeholder={messages["statutory.timeSheet.startDate"]}
                          style={{ width: '100%' }}
                          onChange={(date: Moment) => {
                            form.setFieldsValue({
                              startDate: date.toISOString(),
                            });
                          }}
                          format="DD/MM/YYYY"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name="endDate">
                        <AppDatePicker
                          placeholder={messages["statutory.timeSheet.endDate"]}
                          style={{ width: '100%' }}
                          onChange={(date: Moment) => {
                            form.setFieldsValue({
                              endDate: date.toISOString(),
                            });
                          }}
                          format="DD/MM/YYYY"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>

                <Form.Item label={messages["statutory.timeSheet.taxPeriod"]} name="taxPeriod">
                  <AppSelect
                    defaultValue={taxWeekOption}
                    options={[taxWeekOption]}
                    disabled={true}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>

          {/* Rates Section */}
          <div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '20px',
                padding: '12px 16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
              }}
            >
              <span style={{ fontSize: '18px' }}>💰</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                {messages["statutory.timeSheet.rate"]}
              </h3>
            </div>

            <Table
              columns={rateColumns}
              dataSource={rateItems}
              pagination={false}
              size="small"
              style={{ marginBottom: '16px' }}
            />
          </div>
        </AppFormContainer>
      </AppCard>
    </AppModalContainer>
  );
};

export default AddNewTimeSheet;
